import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, TrendingUp, ChevronDown } from "lucide-react";

const AREAS = [
  "Manhattan Beach", "Beverly Hills", "Santa Monica", "Malibu", "Venice", "Hollywood Hills",
  "Pacific Palisades", "Brentwood", "Hermosa Beach",
  "Redondo Beach", "El Segundo", "Torrance", "Palos Verdes", "Silver Lake", "Calabasas",
];

const fmtPrice = (n) => (typeof n === "number" ? `$${Math.round(n).toLocaleString()}` : "—");
const fmtPsf = (n) => (typeof n === "number" ? `$${Math.round(n).toLocaleString()}` : "—");
const fmtDom = (n) => (typeof n === "number" ? `${Math.round(n)} days` : "—");
const fmtInt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");

export default function MarketSnapshot() {
  const [area, setArea] = useState("Manhattan Beach");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/market/stats`, { params: { location: area } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [area]);

  const stats = [
    { label: "Median sale price", value: fmtPrice(data?.median_sale_price), testid: "stat-median-price" },
    { label: "Median days on market", value: fmtDom(data?.median_days_on_market), testid: "stat-dom" },
    { label: "Avg. price / sqft", value: fmtPsf(data?.average_price_per_sqft), testid: "stat-psf" },
    { label: "Active listings", value: fmtInt(data?.total_listings_sale), testid: "stat-listings" },
  ];

  return (
    <div data-testid="market-snapshot">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="overline flex items-center gap-2"><TrendingUp size={14} className="text-sand" /> Live market data</p>
          <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
            Right now in {area}.
          </h2>
          <p className="mt-3 text-soft text-sm">
            Fresh data across {data?.zip_codes?.length ? data.zip_codes.join(", ") : "—"}
          </p>
        </div>
        <div className="md:max-w-md w-full">
          <label className="overline block mb-3" htmlFor="market-area-select">Choose an area</label>
          <div className="relative">
            <select
              id="market-area-select"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              className="appearance-none w-full border-2 border-soft bg-paper py-5 pl-6 pr-14 text-lg md:text-xl focus:outline-none focus:border-sand hover:border-sand transition-colors cursor-pointer font-medium"
              style={{ fontFamily: "Playfair Display, serif" }}
              data-testid="market-area-select"
            >
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <ChevronDown size={22} className="absolute right-5 top-1/2 -translate-y-1/2 text-sand pointer-events-none" />
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-soft p-8 text-soft text-sm" data-testid="market-error">
          {error}
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s) => (
            <div key={s.label} className="bg-paper border border-soft p-8" data-testid={s.testid}>
              <p className="overline">{s.label}</p>
              <p className="mt-4 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
                {loading ? <Loader2 size={24} className="animate-spin text-sand" /> : s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-8 text-xs text-soft max-w-2xl">
        Data refreshed daily via our market partner. Numbers represent activity across the zip
        codes that define each area.
      </p>
    </div>
  );
}
